import type { ArrayCallback, ArrayPredicate } from 'Core/types';
import type { ServerModel } from 'Modules/serverApi/types';
import { equalByMany } from 'Utils/array';

import Facet from './facet';
import FacetValue, { type FacetValueFull } from './facetValue';

export default class FacetCollection {
  _facets: Facet[];

  constructor(
    responseFacets: ServerModel.FacetCategory[] = [],
    valuesFilter: ArrayPredicate<FacetValueFull> = () => true,
    baseTreeLevelMap: Record<string, number> = {},
  ) {
    this._facets = responseFacets.map((f) => new Facet(f, valuesFilter, baseTreeLevelMap[f.FieldName]));
  }

  get selection(): FacetValueFull[] {
    return this._facets.flatMap((f) => f.facetedSelection);
  }

  sameFacetsAsIn = (collection: FacetCollection): boolean => {
    if (!this.length && !collection.length) {
      return true;
    }
    return equalByMany(
      this._facets,
      collection._facets,
      ['selection', FacetValue.termKey],
      ['values', FacetValue.termKey, 'hitCount'],
    );
  };

  get length(): number {
    return this._facets.length;
  }

  filter(predicate: ArrayPredicate<Facet>, thisArg?: unknown): FacetCollection {
    const newCollection = new FacetCollection();
    newCollection._facets = this._facets.filter(predicate, thisArg);
    return newCollection;
  }

  get = (field: string): Facet | undefined => this._facets.find((f) => f.field === field);

  has = (field: string): boolean => this._facets.some((f) => f.field === field);

  map<R>(func: ArrayCallback<Facet, R>, thisArg?: unknown): R[] {
    return this._facets.map(func, thisArg);
  }

  findIndex(predicate: ArrayPredicate<Facet>, thisArg?: unknown): number {
    return this._facets.findIndex(predicate, thisArg);
  }

  slice(start?: number, end?: number): FacetCollection {
    const newCollection = new FacetCollection();
    newCollection._facets = this._facets.slice(start, end);
    return newCollection;
  }

  hasSelectionAfter(field: string): boolean {
    const index = this.findIndex((facet) => facet.field === field);
    return this.slice(index + 1).selection.length > 0;
  }

  every(predicate: ArrayPredicate<Facet>, thisArg?: unknown): boolean {
    return this._facets.every(predicate, thisArg);
  }

  at = (index: number): Facet | undefined => this._facets[index];
}
